import { Fragment } from 'react'
import styled, { useTheme } from 'styled-components'
import { RecentlyBoughtItemResponseDto } from '@/apis/rest/generated/types'
import { useBackAction } from '@/components/common/back-action/use-back-action'
import { Typo, TypoVariant } from '@/components/common/typography'
import { JustViewedProductList } from '@/components/domains/list/just-viewed-product-list'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import { ProductLocation } from '@/constants/product-location.const'
import { useSearchEventTracker } from '@/containers/event-tracker/search.event-tracker'
import { AdPetType } from '@/graphql/generated/schemas'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'

type HandleClickProductCardTracker = ({
  item,
  index,
  properties,
}: {
  item: ProductTypeDefinedByFrontend
  index: number
  properties?: Record<string, any>
}) => void

type SearchResultRecentlyBoughtProductsProps = {
  recentlyBoughtProducts: RecentlyBoughtItemResponseDto[]
  searchKeyword: string
  searchLocation: string
  petType: AdPetType
  onClickProductCardTracker?: HandleClickProductCardTracker
}

export const SearchResultRecentlyBoughtProducts = ({
  recentlyBoughtProducts,
  searchKeyword,
  searchLocation,
  petType,
  onClickProductCardTracker,
}: SearchResultRecentlyBoughtProductsProps) => {
  const { color } = useTheme()
  const { trackClickSearchResultFrequentProduct } = useSearchEventTracker()

  const backAction = useBackAction('recommend-list-for-search-result')

  const handleClickProduct = ({
    productData,
    boughtCount,
  }: {
    productData: ProductTypeDefinedByFrontend
    boughtCount: number
  }) => {
    trackClickSearchResultFrequentProduct({
      productId: productData.id,
      productName: productData.name,
      boughtCount,
      searchKeyword,
      searchLocation,
      petType,
    })
  }

  return (
    <StyledSearchResultRecentlyBoughtProducts className="recently-bought-products">
      <Typo variant={TypoVariant.Heading6Bold} className="recently-bought-products__title">
        최근 구매한 이 상품은 어때요?
      </Typo>
      <div className="recently-bought-products__list">
        {recentlyBoughtProducts.map(({ product, boughtCount }) => {
          const productData = transformProductTypeDefinedByFrontend(product)
          const isShowJustViewedProduct = backAction.contain(`${product.id.toString()}::bought-product`)

          return (
            <Fragment key={productData.id}>
              <NewProductCard
                product={productData}
                size={PRODUCT_CARD_SIZES.md}
                cartType="HORIZONTAL"
                hasCartButton
                label={{
                  value: `${boughtCount}회 구매`,
                  backgroundColor: color.blue500,
                  variant: 'Body5 Bold',
                }}
                onClickProductCard={() => {
                  handleClickProduct({ productData, boughtCount })
                  backAction.push(`${product.id.toString()}::bought-product`)
                }}
                trackerData={{
                  searchKeyword,
                  searchLocation,
                  addLocation: ProductLocation.SearchResultFrequent,
                  boughtCount,
                  brandId: productData.brand?.id,
                  brandName: productData.brand.name,
                  supplyType: productData.supplyType,
                }}
              />
              {isShowJustViewedProduct && (
                <StyledJustViewedProductListWrapper>
                  <JustViewedProductList
                    name="recommend-list-for-search-result"
                    justViewedProduct={transformProductTypeDefinedByFrontend(product)}
                    onClickProductCardTracker={onClickProductCardTracker}
                    trackerData={{
                      location: 'search',
                      addLocation: 'search_result',
                    }}
                  />
                </StyledJustViewedProductListWrapper>
              )}
            </Fragment>
          )
        })}
      </div>
    </StyledSearchResultRecentlyBoughtProducts>
  )
}

const StyledSearchResultRecentlyBoughtProducts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`

const StyledJustViewedProductListWrapper = styled.div`
  margin-top: 2.4rem;
`
